import type * as YaasTypes from "../../types/yaas";
/**
 * Types for the retail YaaS in-page application form.
 *
 * The form is single-panel: two configurable fields
 * (distribution mode, rewards destination) plus a ToS checkbox and
 * a Submit CTA. Defaults let an applicant submit
 * without filling anything if they want the simplest setup.
 *
 * See `claude-docs/intents/retail-yaas.md` for behavior contracts.
 */

export type DistributionMode = "stream" | "claim";

export interface ApplicationDraft {
    terms?: Readonly<YaasTypes.PublishedTermsDescriptor>;
    distributionMode: DistributionMode;
    /** Solana base58 address. Empty string = use default (connected wallet). */
    rewardsDestination: string;
    /** UI-only account label for the rewards destination (e.g. "Rewards
     *  Wallet"). NOT part of the submit payload — mirrors the real
     *  EnableRewardsForm's local label field. Seedable so sim drivers can
     *  type it in character-by-character. Empty string = unlabeled. */
    accountLabel: string;
    tosAccepted: boolean;
}

/**
 * Per-field validation state.
 *   "default"  — empty / unedited; field reads as neutral
 *                (`border-line-base`).
 *   "success"  — non-empty + valid; field surfaces with
 *                `border-success-base` to confirm the entry parsed.
 *   "error"    — blocks Submit; surfaces with `border-error-base`.
 *   "warning"  — does NOT block Submit; surfaces with
 *                `border-warning-base`.
 */
export type FieldIssueState = "default" | "success" | "error" | "warning";

export interface ApplicationDraftIssues {
    rewardsDestination: FieldIssueState;
}

export const DEFAULT_DRAFT: ApplicationDraft = {
    distributionMode: "stream",
    rewardsDestination: "",
    accountLabel: "",
    tosAccepted: false
};
